import { customElement, scss } from '../../core/ids-decorators';
import { attributes } from '../../core/ids-attributes';
import { stringToBool } from '../../utils/ids-string-utils/ids-string-utils';
import IdsKeyboardMixin from '../../mixins/ids-keyboard-mixin/ids-keyboard-mixin';
import IdsDrawer from '../ids-drawer/ids-drawer';

import '../ids-accordion/ids-accordion';
import '../ids-button/ids-button';
import '../ids-icon/ids-icon';
import '../ids-text/ids-text';
import '../ids-toolbar/ids-toolbar';
import './ids-app-menu-container';
import { getClosest } from '../../utils/ids-dom-utils/ids-dom-utils';

import styles from './ids-app-menu.scss';
import type IdsAccordion from '../ids-accordion/ids-accordion';
import type IdsSearchField from '../ids-search-field/ids-search-field';
import type IdsContainer from '../ids-container/ids-container';

const CONTAINER_CLASS = 'app-menu';
const CONTAINER_OPEN_CLASS = 'app-menu-is-open';

const Base = IdsKeyboardMixin(
  IdsDrawer
);

/**
 * IDS App Menu Component
 * @type {IdsAppMenu}
 * @inherits IdsDrawer
 * @part avatar - the user avatar
 * @part accordion - the accordion root element
 */
@customElement('ids-app-menu')
@scss(styles)
export default class IdsAppMenu extends Base {
  globalKeydownListener?: (e: KeyboardEvent) => void;

  constructor() {
    super();
  }

  #container: IdsContainer | undefined | null;

  connectedCallback() {
    super.connectedCallback();
    this.edge = 'start';
    this.type = 'app-menu';
    this.#connectSearchField();
    this.#setContainer();
    this.#attachEventHandlers();
  }

  disconnectedCallback(): void {
    super.disconnectedCallback?.();
    this.#detachEventHandlers();
    this.#clearContainer();
  }

  static get attributes() {
    return [
      ...super.attributes,
      attributes.KEEP_OPEN,
    ];
  }

  // Slots:
  // - Avatar
  // - Roles (Accordion)
  // - Header Nav (Toolbar)
  // - Searchfield
  // - Content (Accordion)
  // - Footer Nav (Toolbar)
  template() {
    return `<div class="ids-drawer ids-app-menu type-app-menu">
      <div class="ids-app-menu-user">
        <slot name="avatar"></slot>
        <slot name="username"></slot>
      </div>
      <div class="ids-app-menu-header">
        <slot name="header"></slot>
      </div>
      <div class="ids-app-menu-search">
        <slot name="search"></slot>
      </div>
      <div class="ids-app-menu-content">
        <slot></slot>
      </div>
      <div class="ids-app-menu-footer">
        <slot name="footer"></slot>
      </div>
    </div>`;
  }

  /**
   * @readonly
   * @returns {IdsAccordion} reference to an optionally-slotted IdsAccordion element
   */
  get accordion(): IdsAccordion | null {
    return this.querySelector<IdsAccordion>(`ids-accordion:not([slot])`);
  }

  /**
   * Sets whether the app menu should remain open when clicking outside.
   * @param {boolean | string} val - If true, the menu will not close on outside click.
   */
  set keepOpen(val: boolean | string) {
    const boolVal = stringToBool(val);
    if (boolVal) {
      this.setAttribute(attributes.KEEP_OPEN, String(boolVal));
    } else {
      this.removeAttribute(attributes.KEEP_OPEN);
    }
  }

  /**
   * Gets whether the app menu should remain open when clicking outside.
   * @returns {boolean | string} True if the menu should stay open.
   */
  get keepOpen(): boolean | string {
    return stringToBool(this.getAttribute(attributes.KEEP_OPEN));
  }

  /**
   * @readonly
   * @property {boolean} isFiltered true if the inner navigation accordion is currently being filtered
   */
  isFiltered = false;

  #attachEventHandlers() {
    this.#detachEventHandlers();
    this.onEvent('show.app-menu-show', this, () => {
      this.#container?.classList.add(CONTAINER_OPEN_CLASS);
    });
    this.onEvent('hide.app-menu-hide', this, () => {
      this.#container?.classList.remove(CONTAINER_OPEN_CLASS);
    });
    this.refreshTriggerEvents();
  }

  #detachEventHandlers() {
    this.offEvent('show.app-menu-show');
    this.offEvent('hide.app-menu-hide');
  }

  /**
   * Adds CSS class to ids-container for initial CSS rules
   */
  #setContainer() {
    this.#container = getClosest(this, 'ids-app-menu-container');
    if (!this.#container) this.#container = getClosest(this, 'ids-container');
    this.#container?.classList.add(CONTAINER_CLASS);
    this.#container?.classList.toggle(CONTAINER_OPEN_CLASS, Boolean(this.visible));
  }

  #clearContainer() {
    this.#container?.classList.remove(CONTAINER_CLASS);
    this.#container = null;
  }

  /**
   * Attaches a slotted IdsSearchField component to the app menu
   */
  #connectSearchField() {
    const searchfield = this.querySelector<IdsSearchField>('ids-search-field[slot="search"]');
    if (searchfield) {
      searchfield.onSearch = (value: string) => {
        if (value !== '') {
          return this.filterAccordion(value);
        }

        this.clearFilterAccordion();
        return [];
      };
      this.offEvent('cleared.search');
      this.onEvent('cleared.search', searchfield, () => this.clearFilterAccordion());
    }
  }

  /**
   * Inherited from the Popup Open Events Mixin.
   * Runs when a click event is propagated to the window.
   * @param {MouseEvent} e the original click event
   * @returns {void}
   */
  onOutsideClick(e: MouseEvent): void {
    const target = e.target as HTMLElement;
    const ignoreKeepOpen = target?.classList.contains('ignore-keep-open');
    const keepOpen = !ignoreKeepOpen && this.keepOpen;

    if (keepOpen || (this.isEqualNode(target) || this.contains(target))) {
      return;
    }

    // Don't close the popup if md+ media query breakpoint
    if (window.innerWidth < 840) {
      this.hide();
    }
  }

  /**
   * Performs a filter operation on accordion panels
   * @param {string} value text value with which to filter accordion panels
   * @returns {Array<HTMLElement>} containing matching accordion panels
   */
  filterAccordion = (value = '') => {
    // Do nothing if there is no accordion, or there are no accordion panels
    if (!this.accordion) {
      return [];
    }
    const panels = [...this.accordion.querySelectorAll('ids-accordion-panel')];
    if (!panels.length) {
      return [];
    }

    // Always remove previous highlight before applying a new one
    this.clearFilterAccordion();

    const escapedValue = value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const valueRegex = new RegExp(escapedValue, 'gi');

    const markParentPanel = (thisPanel: any) => {
      if (thisPanel.hasParentPanel) {
        const parentPanel = thisPanel.parentElement;
        const parentHeader = parentPanel.header;

        if (!parentPanel.expanded) {
          parentPanel.expanded = true;
        }

        parentHeader.childFilterMatch = true;
        markParentPanel(parentPanel);
      }
    };

    return panels.filter((panel: any) => {
      const header = panel.header;
      const textContent = header.textContent.trim();
      const hasTextMatch = textContent.match(valueRegex);
      if (hasTextMatch) {
        // Highlight
        // NOTE: Apply text highlighter here (See #494)
        if (header.hiddenByFilter) {
          header.hiddenByFilter = false;
        }
        markParentPanel(panel);
        this.isFiltered = true;
        return header;
      }

      // Unhighlight
      if (!header.hiddenByFilter) {
        header.hiddenByFilter = true;
      }
    });
  };

  /**
   * Clears a navigation accordion's previous filter result
   * @private
   */
  clearFilterAccordion() {
    const filteredHeaders: any = [...this.accordion?.querySelectorAll('ids-accordion-header[hidden-by-filter]') ?? []];
    filteredHeaders.map((header: any) => {
      header.hiddenByFilter = false;
      return header;
    });

    // NOTE: Clear text highlighter here (See #494)
    this.#clearChildFilter();
    this.isFiltered = false;
  }

  /**
   * Resets the "child-filter-match" attribute on any accordion headers who's children matched a previous filter result
   * @returns {void}
   */
  #clearChildFilter() {
    const childFilterMatches: any = [...this.accordion?.querySelectorAll('[child-filter-match]') ?? []];
    childFilterMatches.map((header: any) => {
      header.childFilterMatch = false;
      return header;
    });
  }

  /**
   * Overrides `addOpenEvents` from IdsPopupOpenEventsMixin to add a global handler
   * for App Menu keyboard events that can cause the menu to close
   */
  addOpenEvents() {
    super.addOpenEvents();

    this.globalKeydownListener = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopImmediatePropagation();
        this.hide();
      }
    };
    document.addEventListener('keydown', this.globalKeydownListener);
  }

  /**
   * Overrides `removeOpenEvents` from IdsPopupOpenEventsMixin
   */
  removeOpenEvents() {
    super.removeOpenEvents();
    if (this.globalKeydownListener) document.removeEventListener('keydown', this.globalKeydownListener);
  }
}
